import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { tLang } from "@/contexts/PortalLanguageContext";

const CDN   = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";
const EMBER = "#FF6B35";
const NAVY  = "#1A1A2E";

export default function About() {
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";
  return (
    <>
      <Helmet>
        <title>{tLang(locale, "about.meta.title", "About Fyrris")}</title>
        <meta name="description" content={tLang(locale, "about.meta.description")} />
        <meta name="keywords" content="Fyrris about, HPB marketplace, hearth industry AR, BBQ outdoor living platform, fireplace visualization company, AR retail technology" />
        <meta property="og:title" content={tLang(locale, "about.meta.ogTitle")} />
        <meta property="og:description" content={tLang(locale, "about.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang(locale, "about.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang(locale, "about.meta.ogDescription")} />
      </Helmet>

      {/* Hero */}
      <section style={{ position: "relative", height: "65vh", overflow: "hidden" }}>
        <img
          src={`${CDN}/hero/About%20Page%20Hero.png`}
          alt="Fyrris brand — connecting homeowners and HPB specialty retailers through AR"
          loading="eager"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center center",
            zIndex: 0,
          }}
        />
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "linear-gradient(to bottom, rgba(26,26,46,0.85) 0%, rgba(26,26,46,0.75) 100%)",
        }} />
        <div style={{
          position: "relative", zIndex: 10,
          height: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          textAlign: "center",
          padding: "0 1.5rem",
        }}>
          <h1 style={{
            fontSize: "clamp(2.25rem, 5vw, 4rem)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.15,
            margin: 0,
          }}>
            About Fyrris
          </h1>
          <p style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: "1.125rem",
            maxWidth: "36rem",
            marginTop: "1rem",
            lineHeight: "1.75",
          }}>
            {tLang(locale, "about.mission.body", "Fyrris connects homeowners with the products they love and the retailers who sell them — through the power of augmented reality.")}
          </p>
        </div>
      </section>

      <div style={{ position: "relative", zIndex: 20, backgroundColor: "#1A1A2E" }}>

        {/* What We Do */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-gray-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-8">What We Do</h2>
            <ul className="space-y-6 text-gray-300 max-w-3xl">
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Augmented Reality Visualization</strong>
                <span className="leading-relaxed">See products as they would look in your home — at true scale, before you ever visit a showroom.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Smart Retail Locator</strong>
                <span className="leading-relaxed">Discover nearby showrooms and installers instantly. Every AR session surfaces the closest store that carries that product.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Retailer Portal</strong>
                <span className="leading-relaxed">Manage listings, analytics, and customer engagement from one straightforward dashboard.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">AI & 3D Automation</strong>
                <span className="leading-relaxed">Accelerate product onboarding and visualization quality at scale — no technical staff required.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Story */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-gray-800" style={{ marginTop: "4rem" }}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Our Story</h2>
            <div className="space-y-5 text-lg text-gray-300 leading-relaxed max-w-3xl">
              <p>
                Fyrris was created by a team passionate about innovation, design, and human connection.
                We saw an industry rich in craftsmanship and emotion — yet missing digital tools that
                truly reflect it.
              </p>
              <p>
                Today, Fyrris bridges that gap, empowering retailers and manufacturers
                to thrive in an AR-driven future while helping customers design their perfect gathering
                moments.
              </p>
            </div>
          </div>
        </section>

        {/* Vision */}
        <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", marginBottom: "4rem" }} />
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24" style={{ marginTop: "4rem" }}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Our Vision</h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl">
              To become the go-to AR marketplace for the hearth, BBQ, and outdoor living world —
              inspiring people to design their moments and rediscover the joy of being together
              in their outdoor spaces.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 sm:px-6 lg:px-8 pb-24 md:pb-32 border-t border-gray-800 pt-16">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl bg-gray-900 border border-gray-800 text-center" style={{ padding: "3rem 2rem" }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
                Are you a retailer?
              </h2>
              <p className="text-gray-300 max-w-xl mx-auto leading-relaxed text-lg">
                Join the Fyrris network and connect with shoppers who are already looking
                for products like yours — guided straight to your store.
              </p>
              <div style={{ marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <Link
                  to="/retailers"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
                  style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
                >
                  See Retailer Plans →
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
